/**
 * One insight pack's page on one game. The frame points at the API's /view address,
 * which builds the page the FIRST time (in the request — no job) and serves
 * the stored page after; the API answers under a sandbox CSP, and the frame
 * adds the same posture: scripts yes, same-origin NO, so an uploaded page
 * runs in an opaque origin and cannot act as the signed-in user.
 *
 * A refusal (not eligible, build failed) is JSON the frame would render as
 * text, so the listing is read first and again after the frame loads: the
 * run row carries the reason, and it is shown above the frame.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { trackEvent } from '../lib/analytics'
import { ApiError, type Session } from '../lib/api'
import { alignToGame } from '../lib/orgAlign'
import { pathSeg } from '../lib/urls'
import {
  listGamePacks, packViewUrl, rebuildPack, type PackEntry,
} from '../packs/api'
import {
  matchVideoWindowName, useMatchVideoWindow, usePackVideoSource,
} from '../packs/videoHandoff'
import { useCurrentTheme } from '../theme/useCurrentTheme'

import './insights-lab.css'
import './insights-packs.css'

export default function InsightPackViewer({ session }: { session: Session }) {
  const { name, version, gameCode } = useParams<{
    name: string; version: string; gameCode: string
  }>()
  const [entry, setEntry] = useState<PackEntry | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  // Bumped to reload the frame after a rebuild.
  const [nonce, setNonce] = useState(0)

  // The pack page carries no theme toggle of its own (DS-023): it reads
  // ?theme= before first paint and listens for priori.theme messages from
  // its parent. The URL gets a SNAPSHOT — the live theme must not ride in
  // src, since changing src reloads the page and loses the user's place —
  // and changes travel by message instead.
  const frameRef = useRef<HTMLIFrameElement>(null)
  const theme = useCurrentTheme()
  const initialTheme = useRef(theme).current
  const postTheme = useCallback(() => {
    // '*' because the sandboxed frame's origin is null (no way to name
    // it), and the payload is just a theme word — nothing sensitive.
    frameRef.current?.contentWindow?.postMessage(
      { type: 'priori.theme', theme }, '*')
  }, [theme])
  useEffect(() => { postTheme() }, [postTheme])

  // A page that plays the video itself asks for its source (a presigned
  // URL; packs/videoHandoff). One that does not gets the match video in
  // its own window on a double-click, with a link when the browser blocks
  // that — after MOMENT_GRACE_MS, as older pages ask after their first
  // moment.
  const playsInline = usePackVideoSource(frameRef, gameCode)
  const { blockedVideo, cancel: cancelVideo } =
    useMatchVideoWindow(frameRef, gameCode, playsInline)

  const isAdmin = session.user.is_platform_admin
  const isQa = isAdmin || session.user.qa_enabled

  const load = useCallback(() => {
    if (!gameCode || !name || !version) return
    listGamePacks(gameCode)
      .then((d) => setEntry(
        d.pages.find((p) => p.name === name && p.version === version) ?? null))
      .catch(async (e: unknown) => {
        if ((e as { status?: number }).status === 404) {
          const { switched } = await alignToGame(gameCode)
          if (switched) { window.location.reload(); return }
        }
        setError(e instanceof ApiError ? e.message : String(e))
      })
  }, [gameCode, name, version])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    // Insights Lab was never instrumented on the SPA (Mixpanel audit,
    // 2026-10-20): one event per pack page opened.
    if (name && version && gameCode) {
      trackEvent('Insight Pack Opened',
                 { pack: name, version, game_code: gameCode })
    }
  }, [name, version, gameCode])

  if (!gameCode || !name || !version) return <div className="panel error">Bad URL.</div>
  if (error) return <div className="panel error">Could not load this page — {error}</div>
  if (entry === undefined) return <p className="muted">Loading…</p>
  if (entry === null) {
    return (
      <div className="panel error">
        No insight pack {name}@{version} is registered.{' '}
        <Link to={`/manage-insights/game/${pathSeg(gameCode)}`}>Back to the game</Link>
      </div>
    )
  }

  const rebuild = () => {
    const reason = window.prompt('Reason for rebuilding this page?')
    if (!reason) return
    setBusy(true)
    setLoaded(false)
    rebuildPack(gameCode, name, version, reason)
      .then(() => { setNonce((n) => n + 1); load() })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(false))
  }

  const run = entry.last_run
  const src = `${packViewUrl(gameCode, name, version)}&n=${nonce}&theme=${initialTheme}`

  return (
    <div className="insights-viewer">
      <header className="viewer-bar">
        <Link to={`/manage-insights/game/${pathSeg(gameCode)}`} className="viewer-back">
          ← {gameCode}
        </Link>
        <div className="viewer-title">
          <strong>{entry.title}</strong>{' '}
          <span className="muted mono">{name}@{version}</span>
        </div>
        <div className="viewer-actions">
          {isAdmin && entry.eligibility.met && (
            <button type="button" disabled={busy} onClick={rebuild}>
              {busy ? 'Rebuilding…' : 'Rebuild'}
            </button>
          )}
        </div>
      </header>

      {!entry.eligibility.met && (
        <div className="panel error">
          This page cannot be built for this game:
          <ul>
            {entry.eligibility.missing.map((m) => (
              <li key={`${m.kind}-${m.name}`}>
                <span className="mono">{m.kind} {m.name}</span>
                {m.detail ? <span className="muted"> — {m.detail}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {run?.status === 'failed' && run.error && (
        <div className="panel error">The last build failed — {run.error}</div>
      )}

      {entry.eligibility.met && !entry.current && isQa && !isAdmin
        && entry.last_run?.published_at && (
        <div className="panel error">
          This page is published to the club and its data has changed since.
          Rebuilding it retires that publication, which takes an admin.
        </div>
      )}
      {entry.eligibility.met && !entry.current && !isQa && (
        <div className="panel error">
          This page needs building for this game, which takes the QA scope.
          Ask a QA user or an admin to open it once; it is stored after that.
        </div>
      )}
      {blockedVideo && (
        <div className="panel">
          The browser blocked the video window.{' '}
          <a href={blockedVideo} target={matchVideoWindowName(gameCode)}>Open the video</a>
        </div>
      )}

      {entry.eligibility.met && !loaded && (
        <p className="muted">
          {entry.current
            ? 'Loading the stored page…'
            : 'Building this page from the game’s serve tables — the first open can take a minute.'}
        </p>
      )}

      {entry.eligibility.met && (
        <iframe
          ref={frameRef}
          className="viewer-frame"
          title={`${entry.title} — ${gameCode}`}
          src={src}
          onLoad={() => { cancelVideo(); setLoaded(true); postTheme(); load() }}
          sandbox="allow-scripts"
        />
      )}
    </div>
  )
}
